import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ApiUtil from '../utils/apiUtils';
import BlogRenderer from './BlogRenderer';
import './DynamicPage.css';

const BlogPage = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use prop slug if provided, otherwise extract from params or location
  const slug = propSlug || paramSlug || location.pathname.substring(1);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get the correct image source URL
  const getImageSrc = (imageUrl) => {
    // If it's already an absolute URL (S3), use it as is
    if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      return imageUrl;
    }
    // Otherwise, it's a local file path, prepend base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://hdmovietoday.xyz';
    return `${apiBaseUrl.replace('/api', '')}${imageUrl}`;
  };

  useEffect(() => {
    const fetchBlogPage = async () => {
      try {
        setLoading(true);
        
        // First, check if this slug should be redirected
        try {
          const redirectResponse = await ApiUtil.SendAsync({
            url: `/redirect-management/check-redirect?url=/${slug}`,
            method: 'GET'
          });
          
          if (redirectResponse.data && redirectResponse.data.success && redirectResponse.data.redirect) {
            const { targetUrl } = redirectResponse.data.redirect;
            
            if (targetUrl.startsWith('/')) {
              // Internal redirect
              navigate(targetUrl, { replace: true });
              return;
            } else {
              // External redirect
              window.location.href = targetUrl;
              return;
            }
          }
        } catch (redirectError) {
          // No redirect found, proceed to fetch page
        }
        
        // Fetch from BlogManager pages endpoint
        const response = await ApiUtil.SendAsync({
          url: `/pages/${slug}`,
          method: 'GET'
        });
        
        if (response.data) {
          const blogPageData = response.data;
          
          // Convert blog page format to standard format
          const pageData = {
            name: blogPageData.title,
            htmlContent: blogPageData.body, // Editor.js JSON content
            cssContent: blogPageData.customCSS || '',
            seoTitle: blogPageData.metaTitle || blogPageData.title,
            seoDescription: blogPageData.metaDescription || blogPageData.excerpt || '',
            seoKeywords: (blogPageData.metaKeywords || []).join(', '),
            featuredImage: blogPageData.featuredImage,
            excerpt: blogPageData.excerpt,
            createdAt: blogPageData.createdAt
          };
          
          setPage(pageData);
        } else {
          setError('Page not found');
        }
        
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setError('Page not found');
        } else {
          setError('Error loading page');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogPage();
    }
  }, [slug, navigate]);

  // Fix layout issues after page content is rendered
  useEffect(() => {
    if (!page) return;

    const fixFlexContainerLayout = () => {
      try {
        const container = document.querySelector('.blog-page-content');
        if (!container) return;

        const containerWidth = container.offsetWidth;
        if (containerWidth <= 0) return;

        // Find all flex containers and fix layout issues
        const flexContainers = container.querySelectorAll('[style*="display: flex"], [style*="display:flex"]');

        flexContainers.forEach((flexContainer) => {
          try {
            const children = Array.from(flexContainer.children);
            const totalChildren = children.length;

            if (totalChildren === 0) return;

            const containerPadding = 30;
            const availableWidth = containerWidth - containerPadding;
            const currentWidth = flexContainer.offsetWidth;

            if (currentWidth > availableWidth && availableWidth > 0) {
              // Force the flex container to fit within bounds
              flexContainer.style.maxWidth = '100%';
              flexContainer.style.width = '100%';
              flexContainer.style.flexWrap = 'wrap';
              flexContainer.style.boxSizing = 'border-box';

              // Adjust children to fit properly
              children.forEach((child) => {
                try {
                  const childOriginalStyle = child.getAttribute('style') || '';
                  
                  if (childOriginalStyle.includes('width')) {
                    const flexBasis = Math.floor(availableWidth / totalChildren) - 20;
                    if (flexBasis > 0) {
                      child.style.maxWidth = `${flexBasis}px`;
                      child.style.flexShrink = '1';
                      child.style.flexGrow = '1';
                      child.style.boxSizing = 'border-box';
                    }
                  }
                } catch (childError) {
                  // Error handling removed for production
                }
              });
            }
          } catch (containerError) {
            // Error handling removed for production
          }
        });

        // Fix elements with explicit large widths
        const elementsWithWidth = container.querySelectorAll('[style*="width:"], [style*="min-width:"]');
        elementsWithWidth.forEach((element) => {
          try {
            const currentWidth = element.offsetWidth;
            const availableWidth = containerWidth - 30;
            if (currentWidth > availableWidth && availableWidth > 0) {
              element.style.maxWidth = '100%';
              element.style.width = '100%';
              element.style.boxSizing = 'border-box';
            }
          } catch (elementError) {
            // Error handling removed for production
          }
        });
      } catch (error) {
        // Error handling removed for production
      }
    };

    // Run the fix after a short delay to ensure DOM is fully rendered
    setTimeout(fixFlexContainerLayout, 100);

    // Also run on window resize
    window.addEventListener('resize', fixFlexContainerLayout);

    return () => {
      window.removeEventListener('resize', fixFlexContainerLayout);
    };
  }, [page]);

  if (loading) {
    return (
      <div className="dynamic-page-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading page...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="dynamic-page-error">
        <div className="error-container">
          <h1>404 - Page Not Found</h1>
          <p>The page you're looking for doesn't exist or has been moved.</p>
          <button 
            className="back-home-btn"
            onClick={() => navigate('/')}
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.seoTitle || page.name}</title>
        <meta name="description" content={page.seoDescription || ''} />
        <meta name="keywords" content={page.seoKeywords || ''} />
        <meta property="og:title" content={page.seoTitle || page.name} />
        <meta property="og:description" content={page.seoDescription || ''} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/${slug}`} />
      </Helmet>
      
      <div className="dynamic-page-container">
        {/* Inject the custom CSS */}
        {page.cssContent && (
          <style dangerouslySetInnerHTML={{ __html: page.cssContent }} />
        )}
        
        {/* Render BlogManager content using container layout */}
        <div className="container">
          <div className="blog-page-content">
            {/* Featured Image */}
            {page.featuredImage && (
              <div className="page-featured-image mb-4">
                <img
                  src={getImageSrc(page.featuredImage)}
                  alt={page.name}
                  className="img-fluid w-100"
                  style={{ 
                    maxHeight: '500px', 
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div style="height: 300px; display: flex; align-items: center; justify-content: center; background-color: #f8f9fa; border-radius: 8px;">
                        <div class="text-center">
                          <i class="fas fa-image fa-3x text-muted"></i>
                          <p class="mt-2 text-muted">Image unavailable</p>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
            )}
            
            {/* Page Title */}
            <h1 className="page-title mb-4">{page.name}</h1>
            
            {/* Page Excerpt */}
            {page.excerpt && (
              <div className="page-excerpt mb-4 p-4" style={{ 
                background: '#f8f9fa', 
                borderLeft: '4px solid #e67929',
                borderRadius: '0 8px 8px 0'
              }}>
                <p className="lead mb-0">{page.excerpt}</p>
              </div>
            )}
            
            {/* Page Content */}
            <div className="page-content">
              <BlogRenderer data={page.htmlContent || { blocks: [] }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
